/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AclModel,
    AclService,
    L4BackendServiceModel,
    L4FrontendServiceModel,
    L7BackendServiceModel,
    Layer4ApiService,
    Layer7ApiService
} from '../../../shared';
import { ActivatedRoute } from '@angular/router';
import { AfterViewInit, Component, ViewChild, ViewContainerRef } from '@angular/core';
import { BaseComponent } from '../../../../../core/components';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, ModalService, NavigationService, RbacService, UtilsService } from '../../../../../core/services';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-manage-acl',
    templateUrl: './manageACLs.component.html',
    styleUrls: ['./manageACLs.component.scss'],
})

export class ManageACLsComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: any;

    /**
     * To store the table row data.
     */
    public tableRows: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store layer 4 and layer 7 frontends.
     */
    public frontends: any[] = [];

    /**
     * Store selected layer
     */
    public selectedLayer: string = 'layer7';

    /**
     * Store layer 4 acls.
     */
    public l4Acls: AclModel[] = [];

    /**
     * Store layer 4 frontends.
     */
    public l4Frontends: L4FrontendServiceModel[] = [];

    /**
     * Store combined layer 4 and 7 backends.
     * @private
     */
    private backends: L7BackendServiceModel[] | L4BackendServiceModel[] = [];

    /**
     * Store layer 4 and layer 7 acls.
     * @private
     */
    private aclRules: AclModel[] = [];

    /**
     * Template that get emitted to the acl form with values when editing an acl.
     * @private
     */
    private templateSubject: Subject<object> = new Subject<object>();

    constructor(
        public aclService: AclService,
        public navigationService: NavigationService,
        private activatedRoute: ActivatedRoute,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private modalService: ModalService,
        private rbacService: RbacService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Returns our template subject as an observable.
     */
    get template() {
        return this.templateSubject.asObservable();
    }

    public ngAfterViewInit(): void {
        // Get query params
        this.activatedRoute.queryParams.subscribe(params => {
            if (params && params.modal === 'true' && params.backend) {
                this.navigationService.removeParamsFromUrl();
            }
        });
    }

    /**
     * Open acl wizard.
     */
    public openACLWizard(): void {
        if (this.l4Acls.length === this.l4Frontends.length) {
            this.addAcl();
        } else {
            this.openSelectLayerModal();
        }
    }

    /**
     * Toggle ACL layer selection.
     * @param layer
     */
    public toggleLayer(layer: string): void {
        this.selectedLayer = layer;
    }

    /**
     * Confirm acl layer.
     */
    public confirmLayer(): void {
        this.closeLayerSelectModal();
        this.addAcl();
    }

    /**
     * Close layer select modal
     */
    public closeLayerSelectModal(): void {
        this.modalService.close('acl-select-layer');
    }

    /**
     * Confirm ACL layer.
     * @private
     */
    private openSelectLayerModal(): void {
        this.modalService.openModalInParent('acl-select-layer');
    }

    /**
     * Add an ACL.
     * @private
     */
    private addAcl(): void {
        const redirectUrl: string = '/cluster-configuration/manage-acls';
        const redirectText: string = 'manage_acls_return_to';
        this.aclService.addEditAcl(
            this.frontends, this.backends, this.aclRules, redirectUrl, redirectText, null, null, null, this.selectedLayer
        );
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            'l7-frontends': this.layer7ApiService.getFrontends(),
            'l7-backends': this.layer7ApiService.getBackends(),
            'l4-frontends': this.layer4ApiService.getFrontends(),
            'l4-backends': this.layer4ApiService.getBackends(),
            'l7-acls': this.layer7ApiService.getAclRules(),
            'l4-acls': this.layer4ApiService.getAclRules()
        };

        forkJoin(apiCalls).pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    if (dataGroups['l7-frontends']) {
                        this.utilsService.addTypeToService(dataGroups['l7-frontends'], 'layer7', 'Layer 7');
                    }
                    if (dataGroups['l4-frontends']) {
                        this.utilsService.addTypeToService(dataGroups['l4-frontends'], 'layer4', 'Layer 4');
                    }
                    if (dataGroups['l7-backends']) {
                        this.utilsService.addTypeToService(dataGroups['l7-backends'], 'layer7', 'Layer 7');
                    }
                    if (dataGroups['l4-backends']) {
                        this.utilsService.addTypeToService(dataGroups['l7-backends'], 'layer4', 'Layer 4');
                    }

                    this.l4Frontends = dataGroups['l4-frontends'];
                    this.l4Acls = dataGroups['l4-acls'];
                    this.frontends = dataGroups['l7-frontends'].concat(this.l4Frontends);
                    this.backends = dataGroups['l7-backends'].concat(dataGroups['l4-backends']);
                    this.aclRules = dataGroups['l7-acls'].concat(this.l4Acls);

                    this.populateTableRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ManageAclsComponent: setUpPageData - failed to get data for page', error.message);
                }
            });
    }

    /**
     * Populate the data table.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        for (const frontend of this.frontends) {
            if (frontend.type !== 'monitor') {
                const acls: AclModel[] = this.aclRules.filter((item: AclModel) => frontend.aclIds.includes(item.id));
                const conditions: string[] = acls.map((item: AclModel) => item.conditionType);
                this.tableRows.push({
                    frontendLabel: frontend.name,
                    frontendId: frontend.id,
                    aclsCount: acls.length,
                    conditions: this.formatConditions(conditions),
                    templateType: frontend.templateType,
                    templateTypeFriendlyName: frontend.templateTypeFriendlyName
                });
            }
        }
    }

    /**
     * Format conditions
     * @param conditions
     * @private
     */
    private formatConditions(conditions: string[]): string {
        const formattedConditions: any[] = [];
        for (const condition of conditions) {
            formattedConditions.push(this.aclService.conditionTypeReadable(condition));
        }

        const stringOfConditions: string = formattedConditions.join(', ');
        const maxLength: number = 35;

        return stringOfConditions.length > maxLength ? stringOfConditions.slice(0, maxLength) + ' ...' : stringOfConditions;
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'manage-acls-add-acl': this.getNewACLsPaths(),
            'manage-acls-acls-details': this.getACLDetailsViewPaths(),
            'manage-acls-hanging-acls-view': [{ path: '/haproxy/acls/hanging', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Get permissions to view acl details.
     * @private
     */
    private getACLDetailsViewPaths(): any[] {
        return [
            { path: '/haproxy/frontends/:id/acls', methods: ['GET'] },
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] }
        ];
    }

    /**
     * New acl paths
     * @private
     */
    private getNewACLsPaths(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['PUT'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }
}
